import { DRAWER_TRANSITION_DURATION_MS, SidebarDrawer } from "@jugl-web/ui-kit";
import { cx } from "@jugl-web/utils";
import { FC, useCallback, useState } from "react";
import { TicketDetailsSidebarState, TicketDetailsTabId } from "../../types";
import { TicketDetailsSidebarContent } from "./components/TicketDetailsSidebarContent";

export interface TicketDetailsSidebarProps {
  isOpen: boolean;
  ticketId: string;
  initialTabId?: TicketDetailsTabId;
  onClose: () => void;
}

export const TicketDetailsSidebar: FC<TicketDetailsSidebarProps> = ({
  isOpen,
  ticketId,
  initialTabId,
  onClose,
}) => {
  const [nestedState, setNestedState] = useState<TicketDetailsSidebarState>({
    isOpen: false,
    ticketId: "",
  });

  const closeNestedSidebar = useCallback(() => {
    setNestedState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const handleTicketLookup = useCallback(
    (lookupTicketId: string) => {
      const open = () => {
        setNestedState({
          isOpen: true,
          ticketId: lookupTicketId,
          initialTabId: "details",
        });
      };

      // Only 1 level of nesting is supported
      // If the nested sidebar is already open, close it and open again with the new ticket ID
      if (nestedState.isOpen) {
        closeNestedSidebar();

        // If for some reason, the lookup ticket ID happens to be the same as the current ticket ID,
        // just close the nested sidebar immediately
        if (ticketId !== lookupTicketId) {
          window.setTimeout(() => {
            open();
          }, DRAWER_TRANSITION_DURATION_MS + 100);
        }
        return;
      }

      open();
    },
    [closeNestedSidebar, nestedState.isOpen, ticketId]
  );

  return (
    <SidebarDrawer
      className={cx(
        "top-1.5 right-1.5 bottom-1.5 flex h-auto w-[calc(100vw-12px)] max-w-[520px] flex-col overflow-hidden rounded-[20px] border border-solid border-dark-100 shadow-[-13px_0_32px_0_rgba(0,0,0,0.12)] transition-all",
        nestedState.isOpen && "top-5.5 right-5.5 -bottom-2.5"
      )}
      isOpen={isOpen}
      hasBackdrop={false}
      onClose={onClose}
      customHeader={null}
    >
      {ticketId && (
        <TicketDetailsSidebarContent
          key={`${ticketId}-${initialTabId}`}
          ticketId={ticketId}
          initialTabId={initialTabId}
          onClose={onClose}
          onTicketLookup={handleTicketLookup}
        />
      )}
      {/* Nested sidebar */}
      <SidebarDrawer
        className="top-1.5 right-1.5 bottom-1.5 flex h-auto w-[calc(100vw-12px)] max-w-[520px] flex-col overflow-hidden rounded-[20px] border border-solid border-dark-100 shadow-[-13px_0_32px_0_rgba(0,0,0,0.12)]"
        isOpen={nestedState.isOpen}
        hasBackdrop={false}
        onClose={closeNestedSidebar}
        customHeader={null}
      >
        {nestedState.ticketId && (
          <TicketDetailsSidebarContent
            key={`${nestedState.ticketId}-${nestedState.initialTabId}`}
            ticketId={nestedState.ticketId}
            rootTicketId={ticketId}
            initialTabId={nestedState.initialTabId}
            onClose={closeNestedSidebar}
            onTicketLookup={handleTicketLookup}
            onGoBack={closeNestedSidebar}
          />
        )}
      </SidebarDrawer>
    </SidebarDrawer>
  );
};
